import { useRef, useEffect } from 'react';
import { useAuth } from '@/providers/auth';
import type { SessionData } from '@/lib/api/types';
import type { ShopperBasketsV2, ShopperProducts, ShopperSearch } from '@/scapi';
import {
    createEvent,
    getEventMediator,
    type EventMediator,
    type AnalyticsEvent,
    type EventSiteInfo,
} from '@salesforce/storefront-next-runtime/events';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import type { AppConfig } from '@/types/config';
import { ensureAdaptersInitialized } from '@/lib/adapters/engagement/initialize';
import { getAllAdapters, getCookiebotConsentPreferences } from '@/lib/adapters';
import {
    buildBasketEcommerce,
    buildDataLayerEcommerce,
    buildDataLayerUserData,
    buildEnrichedBasketEcommerce,
    getBasketCheckoutParameters,
    CATEGORY_LISTING_ITEM_LIST_NAME,
    getItemListTracking,
    SEARCH_RESULTS_ITEM_LIST,
} from '@/lib/analytics/data-layer-mappers';
import type { AnalyticsItemList } from '@/providers/analytics-item-list-context';
import { useDataLayer } from './use-data-layer';

/**
 * Ensures adapters are initialized and returns the event mediator
 *
 * @param appConfig - The application configuration needed to initialize adapters
 * @returns EventMediator instance or undefined if not available
 */
async function getInitializedMediator(appConfig: AppConfig): Promise<EventMediator | undefined> {
    await ensureAdaptersInitialized(appConfig);
    return getEventMediator(getAllAdapters);
}

/**
 * Helper function to track an event with auth validation and mediator initialization
 *
 * Consent is read from Cookiebot at send time; each adapter filters on its own `consentCategory`.
 *
 * @param authPromise - Promise that resolves when auth is available
 * @param appConfig - The application configuration
 * @param siteInfo - Site identification for multi-site support
 * @param eventType - The type of event to track
 * @param eventData - The event data (without user payload, as payload is added automatically)
 * @returns Promise that resolves when tracking is complete or undefined if auth/mediator is unavailable
 */
async function trackEvent<TEventType extends AnalyticsEvent['eventType']>(
    authPromise: Promise<SessionData | undefined>,
    appConfig: AppConfig,
    siteInfo: EventSiteInfo | undefined,
    eventType: TEventType,
    eventData: Omit<Parameters<typeof createEvent<TEventType>>[1], 'payload'>
): Promise<void> {
    // Wait for auth to be defined before tracking
    const auth = await authPromise;
    if (auth === undefined) {
        // Auth not available - silently fail to not break the app
        return;
    }

    const mediator = await getInitializedMediator(appConfig);
    if (!mediator) {
        return;
    }

    const event = createEvent(eventType, {
        ...eventData,
        payload: {
            userType: auth.userType ?? 'guest',
            encUserId: auth.encUserId ?? undefined,
            usid: auth.usid,
        },
    } as Parameters<typeof createEvent<TEventType>>[1]);
    return void mediator.track(event, siteInfo, await getCookiebotConsentPreferences());
}

/**
 * Analytics hook provides tracking functions
 */
export const useAnalytics = () => {
    const auth = useAuth();
    const appConfig = useConfig();
    const dataLayer = useDataLayer();
    const { site, language } = useSite();
    const siteInfo = { siteId: site.id, localeId: language };

    // Store the promise resolver so we can resolve it when auth becomes available
    const authResolverRef = useRef<((value: SessionData | undefined) => void) | null>(null);

    // Create a promise that resolves when auth is available
    // This promise is updated via useEffect when auth changes
    const authPromiseRef = useRef<Promise<SessionData | undefined>>(
        auth !== undefined
            ? Promise.resolve(auth)
            : new Promise<SessionData | undefined>((resolve) => {
                  authResolverRef.current = resolve;
              })
    );

    // Update the promise when auth changes
    useEffect(() => {
        if (auth !== undefined) {
            // Auth is now available - resolve any pending promises
            if (authResolverRef.current) {
                authResolverRef.current(auth);
                authResolverRef.current = null;
            }
            // Create a new resolved promise for future calls
            authPromiseRef.current = Promise.resolve(auth);
        } else {
            // Auth is undefined - create a new pending promise
            authPromiseRef.current = new Promise<SessionData | undefined>((resolve) => {
                authResolverRef.current = resolve;
            });
        }
    }, [auth]);

    // On the server, return empty functions
    if (typeof window === 'undefined') {
        /* eslint-disable @typescript-eslint/no-empty-function */
        return {
            trackViewPage: () => {},
            trackViewProduct: () => {},
            trackCartItemAdd: () => {},
            trackCheckoutStart: () => {},
            trackCheckoutStep: () => {},
            trackAddPaymentInfo: () => {},
            trackViewSearch: () => {},
            trackViewCategory: () => {},
            trackClickProductInCategory: () => {},
            trackClickProductInSearch: () => {},
            trackViewSearchSuggestions: () => {},
            trackClickSearchSuggestion: () => {},
            trackWishlistItemAdded: () => {},
            trackWishlistItemRemoved: () => {},
            trackWishlistViewed: () => {},
            trackWishlistItemMerged: () => {},
            trackWishlistMerged: () => {},
        };
        /* eslint-enable @typescript-eslint/no-empty-function */
    }

    /**
     * Tracks a page view event
     *
     * Page view events are sent automatically by the PageViewTracker component but this
     * function is provided for manual firing of page views.
     */
    const trackViewPage = async (data: { url: string }) => {
        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'view_page', {
            path: data.url,
        });
    };

    /**
     * Track a product view
     */
    const trackViewProduct = async (data: { product: ShopperProducts.schemas['Product'] }) => {
        dataLayer.track({
            event: 'view_item',
            ecommerce: buildDataLayerEcommerce([data.product], {
                currency: dataLayer.currency,
                affiliation: dataLayer.affiliation,
            }),
            event_type: 'ga4_eec',
        });
        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'view_product', {
            product: data.product,
        });
    };

    /**
     * Track add to cart
     *
     * `eventQuantity` reports the amount added in this event when it differs from the basket
     * line's total quantity (e.g. incrementing an existing line). Pass the untouched basket
     * line as the item so unit pricing is de-totalized correctly; only use `eventQuantity`
     * for single-item adds.
     */
    const trackCartItemAdd = async (data: {
        cartItems: ShopperBasketsV2.schemas['ProductItem'][];
        eventQuantity?: number;
        itemList?: AnalyticsItemList | null;
    }) => {
        const itemListTracking = getItemListTracking(data.cartItems, data.itemList);
        dataLayer.track({
            event: 'add_to_cart',
            ecommerce: buildDataLayerEcommerce(itemListTracking.items, {
                currency: dataLayer.currency,
                affiliation: dataLayer.affiliation,
                ...itemListTracking.context,
                ...(data.eventQuantity !== undefined ? { quantity: data.eventQuantity } : {}),
            }),
            event_type: 'ga4_eec',
        });
        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'cart_item_add', {
            cartItems: data.cartItems,
        });
    };

    /**
     * Track start of checkout process
     */
    const trackCheckoutStart = async (data: {
        basket: ShopperBasketsV2.schemas['Basket'];
        productsByItemId?: Record<string, ShopperProducts.schemas['Product']>;
    }) => {
        dataLayer.track({
            event: 'begin_checkout',
            ecommerce: data.productsByItemId
                ? buildEnrichedBasketEcommerce(data.basket, data.productsByItemId, {
                      currency: dataLayer.currency,
                      affiliation: dataLayer.affiliation,
                  })
                : buildBasketEcommerce(data.basket, {
                      currency: dataLayer.currency,
                      affiliation: dataLayer.affiliation,
                  }),
            event_type: 'ga4_eec',
        });
        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'checkout_start', {
            basket: data.basket,
        });
    };

    /**
     * Track start of given checkout step
     */
    const trackCheckoutStep = async (data: {
        stepName: string;
        stepNumber: number;
        basket: ShopperBasketsV2.schemas['Basket'];
        productsByItemId?: Record<string, ShopperProducts.schemas['Product']>;
        guestEmail?: string | null;
    }) => {
        const normalizedStepName = data.stepName.toLowerCase();
        const checkoutParameters = getBasketCheckoutParameters(data.basket);
        const ecommerce = data.productsByItemId
            ? buildEnrichedBasketEcommerce(data.basket, data.productsByItemId, {
                  currency: dataLayer.currency,
                  affiliation: dataLayer.affiliation,
                  shipping_method: checkoutParameters.shipping_method,
                  shipping_tier: checkoutParameters.shipping_tier,
              })
            : buildBasketEcommerce(data.basket, {
                  currency: dataLayer.currency,
                  affiliation: dataLayer.affiliation,
                  shipping_method: checkoutParameters.shipping_method,
                  shipping_tier: checkoutParameters.shipping_tier,
              });
        const guestEmail = data.guestEmail?.trim();

        if (normalizedStepName === 'payment') {
            dataLayer.track({
                event: 'add_shipping_info',
                ecommerce,
                user_data: buildDataLayerUserData(data.basket, guestEmail ? { email_address: guestEmail } : undefined),
                event_type: 'ga4_eec',
            });
        }

        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'checkout_step', {
            stepName: data.stepName,
            stepNumber: data.stepNumber,
            basket: data.basket,
        });
    };

    /**
     * Track when the shopper submits payment information (GA4 add_payment_info).
     */
    const trackAddPaymentInfo = async (data: {
        basket: ShopperBasketsV2.schemas['Basket'];
        paymentType: string;
        productsByItemId?: Record<string, ShopperProducts.schemas['Product']>;
        guestEmail?: string | null;
    }) => {
        const checkoutParameters = getBasketCheckoutParameters(data.basket);
        const guestEmail = data.guestEmail?.trim();
        const ecommerce = data.productsByItemId
            ? buildEnrichedBasketEcommerce(data.basket, data.productsByItemId, {
                  currency: dataLayer.currency,
                  affiliation: dataLayer.affiliation,
                  payment_type: data.paymentType,
                  shipping_method: checkoutParameters.shipping_method,
              })
            : buildBasketEcommerce(data.basket, {
                  currency: dataLayer.currency,
                  affiliation: dataLayer.affiliation,
                  payment_type: data.paymentType,
                  shipping_method: checkoutParameters.shipping_method,
              });

        dataLayer.track({
            event: 'add_payment_info',
            ecommerce,
            user_data: buildDataLayerUserData(data.basket, guestEmail ? { email_address: guestEmail } : undefined),
            event_type: 'ga4_eec',
        });

        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'checkout_step', {
            stepName: 'place_order',
            stepNumber: 5,
            basket: data.basket,
        });
    };

    /**
     * Track search
     */
    const trackViewSearch = async (data: {
        searchInputText: string;
        searchResultCount: number;
        searchResults: ShopperSearch.schemas['ProductSearchHit'][];
        sort: string;
        refinements: ShopperSearch.schemas['ProductSearchResult']['selectedRefinements'];
    }) => {
        dataLayer.track({
            event: 'search',
            search_term: data.searchInputText,
            search_result_count: data.searchResultCount,
            event_type: 'ga4_ce',
        });
        dataLayer.track({
            event: 'view_item_list',
            ecommerce: buildDataLayerEcommerce(data.searchResults, {
                currency: dataLayer.currency,
                affiliation: dataLayer.affiliation,
                ...SEARCH_RESULTS_ITEM_LIST,
                includeValue: false,
            }),
            event_type: 'ga4_eec',
        });
        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'view_search', {
            searchInputText: data.searchInputText,
            searchResults: data.searchResults,
            sort: data.sort || '',
            refinements: data.refinements || {},
        });
    };

    /**
     * Track category view
     */
    const trackViewCategory = async (data: {
        category: ShopperProducts.schemas['Category'];
        searchResults: ShopperSearch.schemas['ProductSearchHit'][];
        sort: string;
        refinements: ShopperSearch.schemas['ProductSearchResult']['selectedRefinements'];
    }) => {
        dataLayer.track({
            event: 'view_item_list',
            ecommerce: buildDataLayerEcommerce(data.searchResults, {
                currency: dataLayer.currency,
                affiliation: dataLayer.affiliation,
                itemListId: data.category.id,
                itemListName: CATEGORY_LISTING_ITEM_LIST_NAME,
                includeValue: false,
            }),
            event_type: 'ga4_eec',
        });
        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'view_category', {
            category: data.category,
            searchResults: data.searchResults,
            sort: data.sort || '',
            refinements: data.refinements || {},
        });
    };

    /**
     * Track click on a product in a category.
     * GA4 `select_item` is sent by the tile, which knows its list position.
     */
    const trackClickProductInCategory = async (data: {
        category: ShopperProducts.schemas['Category'];
        product: ShopperSearch.schemas['ProductSearchHit'];
    }) => {
        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'click_product_in_category', {
            category: data.category,
            product: data.product,
        });
    };

    /**
     * Track click on a product in a search.
     * GA4 `select_item` is sent by the tile, which knows its list position.
     */
    const trackClickProductInSearch = async (data: {
        searchInputText: string;
        product: ShopperSearch.schemas['ProductSearchHit'];
    }) => {
        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'click_product_in_search', {
            searchInputText: data.searchInputText,
            product: data.product,
        });
    };

    /**
     * Track view of search suggestions
     */
    const trackViewSearchSuggestions = async (data: { searchInputText: string; suggestions: Array<string> }) => {
        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'view_search_suggestion', {
            searchInputText: data.searchInputText,
            suggestions: data.suggestions,
        });
    };

    /**
     * Track click on a search suggestion
     */
    const trackClickSearchSuggestion = async (data: { searchInputText: string; suggestion: string }) => {
        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'click_search_suggestion', {
            searchInputText: data.searchInputText,
            suggestion: data.suggestion,
        });
    };

    /**
     * Track wishlist item added
     */
    const trackWishlistItemAdded = async (data: {
        surface: 'pdp' | 'plp' | 'cart' | 'wishlist-page';
        productId: string;
        product?: unknown;
        itemList?: AnalyticsItemList | null;
    }) => {
        const itemSource = {
            ...(data.product && typeof data.product === 'object' ? data.product : {}),
            productId: data.productId,
            quantity: 1,
        };
        const itemListTracking = getItemListTracking([itemSource], data.itemList);
        const ecommerce = buildDataLayerEcommerce(itemListTracking.items, {
            currency: dataLayer.currency,
            affiliation: dataLayer.affiliation,
            ...itemListTracking.context,
        });

        dataLayer.track({
            event: 'add_to_wishlist',
            ecommerce,
            event_type: 'ga4_eec',
        });
        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'wishlist_item_added', {
            surface: data.surface,
            productId: data.productId,
        });
    };

    /**
     * Track wishlist item removed
     */
    const trackWishlistItemRemoved = async (data: {
        surface: 'pdp' | 'plp' | 'cart' | 'wishlist-page';
        productId: string;
    }) => {
        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'wishlist_item_removed', {
            surface: data.surface,
            productId: data.productId,
        });
    };

    /**
     * Track wishlist page view
     */
    const trackWishlistViewed = async () => {
        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'wishlist_viewed', {});
    };

    /**
     * Track individual product merged from guest to registered wishlist
     */
    const trackWishlistItemMerged = async (data: { productId: string }) => {
        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'wishlist_item_merged', {
            productId: data.productId,
        });
    };

    /**
     * Track wishlist merge operation summary on login
     */
    const trackWishlistMerged = async (data: {
        merged: number;
        skipped: number;
        failed: number;
        mergedProductIds: string[];
        skippedProductIds: string[];
        failedProductIds: string[];
    }) => {
        return trackEvent(authPromiseRef.current, appConfig, siteInfo, 'wishlist_merged', data);
    };

    return {
        trackViewPage,
        trackViewProduct,
        trackCartItemAdd,
        trackCheckoutStart,
        trackCheckoutStep,
        trackAddPaymentInfo,
        trackViewSearch,
        trackViewCategory,
        trackClickProductInCategory,
        trackClickProductInSearch,
        trackViewSearchSuggestions,
        trackClickSearchSuggestion,
        trackWishlistItemAdded,
        trackWishlistItemRemoved,
        trackWishlistViewed,
        trackWishlistItemMerged,
        trackWishlistMerged,
    };
};
